import { useState, useEffect, useContext } from "react";
import { Container, Row, Col, Form, Button, Spinner } from "react-bootstrap";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import "../../../assets/css/admin/adminSettings/adminfaqs.css";

const AUDIENCE_OPTIONS = [
  { value: "customer", emoji: "🧳", label: "Customer", desc: "Visible on public FAQ page", cls: "customer" },
  { value: "vendor",   emoji: "🏪", label: "Vendor",   desc: "Shown in vendor dashboard",  cls: "vendor"   },
  { value: "general",  emoji: "🌐", label: "General",  desc: "Visible to everyone",         cls: "general"  },
];
 
const CATEGORY_OPTIONS = [
  { value: "",                    label: "— None —"              },
  { value: "booking_payments",    label: "Booking & Payments"    },
  { value: "account_login",       label: "Account & Login"       },
  { value: "tours_packages",      label: "Tours & Packages"      },
  { value: "cancellation_refund", label: "Cancellation & Refund" },
  { value: "technical_support",   label: "Technical Support"     },
  { value: "general",             label: "General"               },
];

const getCharClass = (n, max) => {
  if (n > max * 0.9)  return "faq-char-over";
  if (n > max * 0.75) return "faq-char-near";
  return "";
};

const EMPTY_FORM = {
  faq_question:  "",
  faq_answer:    "",
  faq_for:       "",
  faq_category:  "",
  display_order: 0,
  is_active:     true,
};

const normalizeFaqFor = (val) => {
  if (!val) return "";
  const trimmed = val.trim().toLowerCase();
 const valid = ["customer", "vendor", "general"];
  return valid.includes(trimmed) ? trimmed : "";
};

export default function FaqCreate({ initialData, onCancel, onSaved }) {
  const { baseUrl, authToken } = useContext(AppContext);
  const isEditing = !!initialData;

  const buildForm = (data) => ({
    faq_question:  data.faq_question  ?? "",
    faq_answer:    data.faq_answer    ?? "",
    faq_for:       normalizeFaqFor(data.faq_for),
    faq_category:  data.faq_category  ?? "",
    display_order: data.display_order ?? 0,
    is_active:     data.is_active     ?? true,
  });

  const [form, setForm] = useState(isEditing ? buildForm(initialData) : EMPTY_FORM);

  useEffect(() => {
    if (isEditing && initialData?.id) {
      setForm(buildForm(initialData));
    } else if (!isEditing) {
      setForm(EMPTY_FORM);
    }
  }, [initialData, isEditing]);

  const [errors, setErrors]       = useState({});
  const [audOpen, setAudOpen]     = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved]         = useState(false);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const selectAud = (val) => {
    setField("faq_for", val);
    setAudOpen(false);
  };

  const validate = () => {
    const e = {};
    if (!form.faq_question.trim()) e.faq_question = "Question is required.";
    if (!form.faq_answer.trim())   e.faq_answer   = "Answer is required.";
    if (!form.faq_for)             e.faq_for       = "Please select an audience.";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      return;
    }
    setSubmitting(true);

    const apiCall = isEditing
      ? axios.put(`${baseUrl}/admin/faqs/${initialData.id}`, form, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
      : axios.post(`${baseUrl}/admin/faqs`, form, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

    apiCall
      .then((response) => {
        if (response.data.success) {
          setSaved(true);
          if (onSaved && typeof onSaved === "function") onSaved(response.data.data);
          setTimeout(() => setSaved(false), 2000);
          if (!isEditing) setForm(EMPTY_FORM);
        } else {
          setErrors({ form: response.data.message || "Operation failed" });
        }
      })
      .catch((err) => {
        console.error(err);
        if (err.response?.data?.errors) {
          setErrors(err.response.data.errors);
        } else {
          setErrors({ form: err.message || "An unexpected error occurred." });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const selectedAud = AUDIENCE_OPTIONS.find(o => o.value === form.faq_for);

  return (
    <div className="faq-page">
      <Container>
        <div className="faq-card">
          {/* Header */}
          <div className="faq-header">
            <span className="faq-header-watermark">FAQ</span>
            <div className="faq-header-icon">
              {isEditing ? (
                <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
                  <path d="M15 4l3 3-9 9H6v-3L15 4z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M13 6l3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg viewBox="0 0 22 22" fill="none" width={22} height={22}>
                  <circle cx="11" cy="11" r="9" stroke="rgba(255,255,255,0.7)" strokeWidth="1.5"/>
                  <path d="M8.5 8.5C8.5 7.12 9.62 6 11 6s2.5 1.12 2.5 2.5c0 1.5-1.5 2-1.5 3.5"
                    stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="11" cy="15.5" r="0.8" fill="white"/>
                </svg>
              )}
            </div>
            <div>
              <div className="faq-header-title">
                {isEditing ? "Edit FAQ Entry" : "New FAQ Entry"}
              </div>
              <div className="faq-header-sub">
                {isEditing
                  ? "Update the question & answer below"
                  : "Add a question & answer to the knowledge base"}
              </div>
            </div>
            <div className="faq-header-badge">FAQ Management</div>
          </div>

          <div className="faq-body">
            {saved && (
              <div className="faq-alert-success">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="7" stroke="#2C5F7C" strokeWidth="1.2"/>
                  <path d="M4.5 8l2.5 2.5 4.5-4.5" stroke="#2C5F7C" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
                {isEditing ? "FAQ successfully updated!" : "FAQ successfully saved!"}
              </div>
            )}

            {errors.form && (
              <div className="faq-alert-error mb-3">{errors.form}</div>
            )}

            <Form onSubmit={handleSubmit} noValidate>
              <div className="faq-section-label" style={{ marginTop: 0 }}>
                Content <div className="faq-section-line" />
              </div>

              {/* Question */}
              <Form.Group className="mb-3">
                <div className="faq-field-label">
                  Question <span className="faq-req">*</span>
                </div>
                <input
                  type="text"
                  className={`faq-input-base ${errors.faq_question ? "error" : ""}`}
                  maxLength={500}
                  placeholder="Write a clear, specific question…"
                  value={form.faq_question}
                  onChange={e => setField("faq_question", e.target.value)}
                />
                <div className="faq-field-footer">
                  <span className="faq-hint">Be specific — avoid vague phrasing</span>
                  <span className={`faq-char-count ${getCharClass(form.faq_question.length, 500)}`}>
                    {form.faq_question.length} / 500
                  </span>
                </div>
                {errors.faq_question && (
                  <div className="faq-error-msg">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="5" stroke="#dc2626" strokeWidth="1.2"/>
                      <path d="M6 3.5v3M6 8.2v.5" stroke="#dc2626" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    {errors.faq_question}
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <div className="faq-field-label">
                  Answer <span className="faq-req">*</span>
                </div>
                <textarea
                  className={`faq-input-base faq-textarea ${errors.faq_answer ? "error" : ""}`}
                  maxLength={5000}
                  placeholder="Write a complete, helpful answer…"
                  value={form.faq_answer}
                  onChange={e => setField("faq_answer", e.target.value)}
                />
                <div className="faq-field-footer">
                  <span className="faq-hint">Tip: be concise and structured</span>
                  <span className={`faq-char-count ${getCharClass(form.faq_answer.length, 5000)}`}>
                    {form.faq_answer.length.toLocaleString()} / 5,000
                  </span>
                </div>
                {errors.faq_answer && (
                  <div className="faq-error-msg">
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="5" stroke="#dc2626" strokeWidth="1.2"/>
                      <path d="M6 3.5v3M6 8.2v.5" stroke="#dc2626" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                    {errors.faq_answer}
                  </div>
                )}
              </Form.Group>

              <div className="faq-section-label">
                Settings <div className="faq-section-line" />
              </div>

              <Row className="g-3 mb-3">
                <Col md={4}>
                  <div className="faq-field-label">
                    Audience <span className="faq-req">*</span>
                  </div>
                  <div
                    className="position-relative"
                    onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setAudOpen(false); }}
                    tabIndex={-1}
                  >
                    <div
                      className={`faq-aud-display ${audOpen ? "open" : ""} ${errors.faq_for ? "error" : ""}`}
                      onClick={() => setAudOpen(prev => !prev)}
                    >
                      <span style={{ fontSize: 16, lineHeight: 1 }}>
                        {selectedAud ? selectedAud.emoji : "👤"}
                      </span>
                      <span style={{
                        fontSize: 14,
                        fontWeight: selectedAud ? 500 : 300,
                        color: selectedAud ? "#0f2030" : "#8fa3b0",
                        flex: 1,
                      }}>
                        {selectedAud ? selectedAud.label : "Select audience…"}
                      </span>
                      <svg
                        width="12" height="7" viewBox="0 0 12 7" fill="none"
                        style={{
                          position: "absolute", right: 13, top: "50%",
                          transform: audOpen ? "translateY(-50%) rotate(180deg)" : "translateY(-50%)",
                          transition: "transform 0.2s", pointerEvents: "none",
                        }}
                      >
                        <path d="M1 1l5 5 5-5" stroke="#8fa3b0" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </div>

                    <div className={`faq-aud-dropdown ${audOpen ? "open" : ""}`}>
                      {AUDIENCE_OPTIONS.map(opt => (
                        <div
                          key={opt.value}
                          className={`faq-aud-option ${form.faq_for === opt.value ? "selected" : ""}`}
                          onClick={() => selectAud(opt.value)}
                        >
                          <div className={`faq-aud-opt-icon ${opt.cls}`}>{opt.emoji}</div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 500, color: "#0f2030" }}>{opt.label}</div>
                            <div style={{ fontSize: 12, color: "#8fa3b0", marginTop: 1 }}>{opt.desc}</div>
                          </div>
                          <div className={`faq-aud-check ${form.faq_for === opt.value ? "visible" : ""}`}>
                            <svg viewBox="0 0 9 7" fill="none" width={9} height={9}>
                              <path d="M1 3.5l2.5 2.5 4.5-4.5" stroke="white" strokeWidth="1.6" strokeLinecap="round"/>
                            </svg>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {errors.faq_for && (
                    <div className="faq-error-msg">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <circle cx="6" cy="6" r="5" stroke="#dc2626" strokeWidth="1.2"/>
                        <path d="M6 3.5v3M6 8.2v.5" stroke="#dc2626" strokeWidth="1.2" strokeLinecap="round"/>
                      </svg>
                      {errors.faq_for}
                    </div>
                  )}
                </Col>

                <Col md={4}>
                  <div className="faq-field-label">
                    Category <span className="faq-opt">optional</span>
                  </div>
                  <select
                    className="faq-input-base faq-select"
                    value={form.faq_category}
                    onChange={e => setField("faq_category", e.target.value)}
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </Col>

                <Col md={4}>
                  <div className="faq-field-label">
                    Display Order <span className="faq-opt">optional</span>
                  </div>
                  <input
                    type="number"
                    className="faq-input-base faq-number-input"
                    min={0} max={999}
                    value={form.display_order}
                    onChange={e => setField("display_order", Number(e.target.value))}
                  />
                  <div className="faq-field-footer">
                    <span className="faq-hint">Lower number = appears first</span>
                  </div>
                </Col>
              </Row>

              <div className="faq-toggle-box">
                <div className="faq-toggle-left">
                  <div className="faq-toggle-icon">👁️</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: "#0f2030" }}>Publish immediately</div>
                    <div style={{ fontSize: 12, color: "#8fa3b0", marginTop: 2 }}>
                      FAQ will be live and visible to selected audience
                    </div>
                  </div>
                </div>
                <label style={{ margin: 0, cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    className="faq-switch-input"
                    checked={form.is_active}
                    onChange={e => setField("is_active", e.target.checked)}
                  />
                  <span className="faq-switch-track" />
                </label>
              </div>

              <div className="faq-divider" />

              <div className="faq-actions">
                <span className="faq-save-hint">
                  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="7" r="5.5" stroke="#8fa3b0" strokeWidth="1.2"/>
                    <path d="M7 4.5v3l2 1" stroke="#8fa3b0" strokeWidth="1.2" strokeLinecap="round"/>
                  </svg>
                  Fields validated on submit
                </span>
                <Button type="button" className="faq-btn-ghost" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="submit" className="faq-btn-primary" disabled={submitting}>
                  {submitting ? (
                    <>
                      <Spinner as="span" size="sm" animation="border" role="status"
                        aria-hidden="true" style={{ width: 14, height: 14 }} />
                      {isEditing ? "Updating…" : "Saving…"}
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 7l3.5 3.5L12 3" stroke="white" strokeWidth="1.8"
                          strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {isEditing ? "Update FAQ" : "Save FAQ"}
                    </>
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </div>
      </Container>
    </div>
  );
}